import type { Route } from "vue-router";
import { APP_TITLE, resolvePageTitle } from "@/utils/page-title";

type MatomoCommand = [string, ...Array<string | number>];

type MatomoConfig = {
  url: string;
  siteId: string;
};

type MatomoEventOptions = {
  category: string;
  action: string;
  name?: string;
  value?: number;
};

declare global {
  interface Window {
    _paq?: MatomoCommand[];
  }
}

function getMatomoConfig(): MatomoConfig | null {
  const url = process.env.VUE_APP_MATOMO_URL?.trim();
  const siteId = process.env.VUE_APP_MATOMO_SITE_ID?.trim();

  if (!url || !siteId) return null;

  return {
    url: url.endsWith("/") ? url : `${url}/`,
    siteId,
  };
}

export function initMatomo() {
  const config = getMatomoConfig();
  if (!config) return;

  window._paq = window._paq ?? [];
  window._paq.push(["setTrackerUrl", `${config.url}matomo.php`]);
  window._paq.push(["setSiteId", config.siteId]);
  window._paq.push(["enableLinkTracking"]);

  if (document.getElementById("matomo-tracker")) return;

  const script = document.createElement("script");
  script.id = "matomo-tracker";
  script.async = true;
  script.src = `${config.url}matomo.js`;

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
  else document.head.appendChild(script);
}

export function trackMatomoPageView(route?: Route) {
  if (!window._paq) return;

  const pageTitle = resolvePageTitle(route);

  window._paq.push(["setCustomUrl", window.location.href]);
  window._paq.push(["setDocumentTitle", pageTitle || document.title || APP_TITLE]);
  window._paq.push(["trackPageView"]);
}

export function setMatomoUserContext(userId?: string, codigoUg?: string) {
  if (!window._paq) return;

  if (userId) window._paq.push(["setUserId", userId]);
  if (codigoUg) {
    window._paq.push(["setCustomVariable", 1, "UG", codigoUg, "visit"]);
  }
}

export function clearMatomoUserContext() {
  if (!window._paq) return;

  window._paq.push(["resetUserId"]);
  window._paq.push(["deleteCustomVariable", 1, "visit"]);
}

export function trackMatomoEvent({ category, action, name, value }: MatomoEventOptions) {
  if (!window._paq) return;

  const event: MatomoCommand = ["trackEvent", category, action];
  if (name) event.push(name);
  if (typeof value === "number" && Number.isFinite(value)) {
    if (!name) event.push("");
    event.push(Math.max(0, Math.round(value)));
  }

  window._paq.push(event);
}

export function trackProtocoloMatomoEvent(action: string, name?: string) {
  trackMatomoEvent({
    category: "Protocolo",
    action,
    name,
  });
}
